import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchShareholderById } from '../shareholdersApi';
import {
  normalizeShareStockType,
  normalizeShareholderType,
  shareholderTypeLabel,
  shareStockTypeLabel,
} from '../shareholderFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';
import './DecisionFormPage.css';

function ShareholderDetailPage() {
  const { language, direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const shareholderId = Number(idParam);
  const invalidId = !Number.isFinite(shareholderId) || shareholderId < 1;
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(!invalidId);
  const [loadError, setLoadError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSecretaryContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    if (invalidId) {
      setLoadError(t('shareholders.invalidId'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const row = await fetchShareholderById(shareholderId);
        if (!cancelled) setItem(row);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('shareholders.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, shareholderId, invalidId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>;

  return (
    <div className="decision-v2" dir={direction}>
      <div className="page-wrapper">
      {loadError && <div className="v2-error">{loadError}</div>}
      {loading && <div className="v2-loading">{t('shareholders.loading')}</div>}
      {!loading && item && (
        <div className="card">
          <div className="card-top">
            <div className="top-bar top-bar--single-line">
              <div className="page-title-row">
                <h1 className="page-title">{t('shareholders.pageTitle')}<span className="page-title-icon"><Icon name="money" size={16} color="#0EA5A0" /></span></h1>
              </div>
              <div className="action-btns">
                <button type="button" className="btn btn-ghost" onClick={() => navigate('/app/shareholders')}>{t('common.back')}</button>
                <button type="button" className="btn btn-primary" onClick={() => navigate(`/app/shareholders/${item.id}/edit`)}>{t('shareholders.edit')}</button>
              </div>
            </div>
          </div>
          <div className="card-inner">
          <div style={{ display: 'flex', flexDirection: 'row', gap: 10, marginBottom: 18, alignItems: 'stretch' }}>
            {[t('shareholders.stepBasic'), t('shareholders.stepContact'), t('shareholders.stepBank')].map((label, i) => {
              const active = i === step;
              return (
                <div key={label} style={{ textAlign: 'center', flex: 1, color: active ? '#0EA5A0' : '#8AACAC', fontSize: '0.82rem' }}>
                  <button type="button" onClick={() => setStep(i)} style={{ width: '100%', border: `1.5px solid ${active ? '#0EA5A0' : '#DDE6E6'}`, background: active ? '#E0F7F6' : '#fff', padding: '10px 8px', fontWeight: 800, position: 'relative', cursor: 'pointer', color: 'inherit', fontFamily: 'Cairo, sans-serif' }}>
                    <span style={{ display: 'inline-block', minWidth: 20, marginInlineEnd: 6 }}>{String(i + 1)}</span>{label}
                    <span style={{ position: 'absolute', right: 0, left: 0, bottom: -1, height: 3, background: active ? '#0EA5A0' : 'transparent' }} />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mform">
            {step === 0 && <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('shareholders.lblName')}</label><div className="mform__input-wrap"><Icon name="user" size={14} color="#02c3a6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.name || ''} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('shareholders.lblType')}</label><div className="mform__input-wrap"><Icon name="money" size={14} color="#1b8eff" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={shareholderTypeLabel(normalizeShareholderType(item.type), language)} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('shareholders.lblStock')}</label><div className="mform__input-wrap"><Icon name="chart" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={shareStockTypeLabel(normalizeShareStockType(item.shareStockType), language)} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('shareholders.lblOwnership')}</label><div className="mform__input-wrap"><Icon name="pie-chart" size={14} color="#ff8c42" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.ownership} dir="ltr" disabled /></div></div>
            </div>}
            {step === 0 && <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('shareholders.lblUserName')}</label><div className="mform__input-wrap"><Icon name="user" size={14} color="#455a64" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.userName && String(item.userName).trim() ? item.userName : '—'} dir="ltr" disabled readOnly /></div></div>
              <div className="mform__field mform__field--span2"><label className="mform__label">{t('shareholders.lblBranch')}</label><div className="mform__input-wrap"><Icon name="branch" size={14} color="#0ea5a4" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.branch || ''} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('common.status')}</label><div className="mform__input-wrap"><Icon name="settings" size={14} color="#14b8a6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.status === 'active' ? t('common.active') : t('common.inactive')} disabled /></div></div>
            </div>}
            {step === 1 && <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('common.phone')}</label><div className="mform__input-wrap"><Icon name="chat" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.phone || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('common.email')}</label><div className="mform__input-wrap"><Icon name="chat" size={14} color="#ff8c42" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.email || ''} dir="ltr" disabled /></div></div>
            </div>}
            {step === 2 && <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('shareholders.lblBankName')}</label><div className="mform__input-wrap"><Icon name="branch" size={14} color="#0ea5a4" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.bankName || ''} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('shareholders.lblBankAccountNumber')}</label><div className="mform__input-wrap"><Icon name="money" size={14} color="#1b8eff" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.bankAccountNumber || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field mform__field--span2"><label className="mform__label">{t('shareholders.lblIban')}</label><div className="mform__input-wrap"><Icon name="chart" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.iban || ''} dir="ltr" disabled /></div></div>
            </div>}
          </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default ShareholderDetailPage;
